import {
  ApolloClient,
  gql,
  NormalizedCacheObject,
  useMutation,
  useQuery,
} from '@apollo/client';
import {Mutation, Query} from '../types/generated/graphql';

/**
 * One-shot fetch for the admin command-center page. Polls every
 * 10s so admins can see heartbeat/idle transitions without
 * refreshing manually. Server returns a fully-joined snapshot —
 * see the `getAdminCommandCenter` resolver for the join shape.
 */
export const useGetAdminCommandCenter = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Query, 'getAdminCommandCenter'>;
  return useQuery<T>(QUERY, {
    client,
    fetchPolicy: 'cache-and-network',
    // COST-04: bumped from 10s → 30s on 2026-06-03. Command Center
    // is a single-operator surface; a 30s refresh is plenty for
    // "what's wrong now?" and was costing ~$15–40/mo in Firestore
    // reads from the joined profile+session+device query.
    pollInterval: 30_000,
  });
};

const QUERY = gql`
  query getAdminCommandCenter {
    getAdminCommandCenter {
      generatedAt
      totals {
        profiles
        unclaimedProfiles
        signedOutProfiles
        profilesWithActiveDevice
        sessions
        activeSessions
        idleSessions
        expiredSessions
        newSessions
        orphanSessions
        ownersWithProfiles
      }
      profiles {
        profileId
        name
        email
        thumbnailUrl
        channelHandle
        owner {
          uid
          email
          phoneNumber
          displayName
          linkedAt
        }
        sessions {
          sessionId
          profileId
          expiresAt
          lastSeenAt
          state
        }
        devices {
          deviceId
          type
          name
          appVersion
          lastSeenAt
          isActive
        }
        status {
          youtubeSessionValid
          youtubeSessionLastCheckedAt
          youtubeSessionInvalidReason
          youtubeSignedOut
          youtubeSignedOutAt
          lastTaskAt
          lastTaskType
        }
      }
      orphanSessions {
        sessionId
        profileId
        expiresAt
        lastSeenAt
        state
      }
      devices {
        deviceId
        type
        name
        appVersion
        ownerUid
        ownerEmail
        ownerDisplayName
        registeredAt
        lastSeenAt
        isActive
        subscribedProfileIds
      }
    }
  }
`;

/**
 * DESTRUCTIVE — wipe a profile and every Firestore record keyed
 * to it. Returns per-collection deletion counts. Refetches the
 * command center after success so the row disappears from the
 * profile list. Per-video scores in `ai_analysis` are intentionally
 * preserved; the purge is profile-scoped only.
 */
export const useAdminPurgeProfile = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'adminPurgeProfile'>;
  return useMutation<T, {profileId: string}>(MUTATION_ADMIN_PURGE_PROFILE, {
    client,
    refetchQueries: ['getAdminCommandCenter'],
  });
};

const MUTATION_ADMIN_PURGE_PROFILE = gql`
  mutation adminPurgeProfile($profileId: ID!) {
    adminPurgeProfile(profileId: $profileId) {
      profileId
      profileExisted
      profileStatusDeleted
      sessionsDeleted
      tasksDeleted
      feedSnapshotsDeleted
      historySnapshotsDeleted
      relatedSnapshotsDeleted
      correctiveActionsDeleted
      feedbackDeleted
      cacheProfileFeedDeleted
      errors
    }
  }
`;

/**
 * DESTRUCTIVE — delete a single session by sessionId. Cascades to
 * any edge tasks linked by sessionId. Does not touch profile,
 * profile_status, snapshots, etc. — use this when one session in
 * a profile's row is stale/corrupted but the profile itself stays.
 */
export const useAdminDeleteSession = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'adminDeleteSession'>;
  return useMutation<T, {sessionId: string}>(MUTATION_ADMIN_DELETE_SESSION, {
    client,
    refetchQueries: ['getAdminCommandCenter'],
  });
};

const MUTATION_ADMIN_DELETE_SESSION = gql`
  mutation adminDeleteSession($sessionId: ID!) {
    adminDeleteSession(sessionId: $sessionId) {
      sessionId
      sessionDeleted
      tasksDeleted
      errors
    }
  }
`;

/**
 * Bulk session sweep for a profile, filtered by AdminCcSessionState
 * (e.g. EXPIRED + IDLE). Cascades to linked edge tasks. Use from a
 * "Sweep expired sessions" button on the profile row.
 */
export const useAdminSweepProfileSessions = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'adminSweepProfileSessions'>;
  return useMutation<T, {profileId: string; states: string[]}>(
    MUTATION_ADMIN_SWEEP_PROFILE_SESSIONS,
    {client, refetchQueries: ['getAdminCommandCenter']},
  );
};

const MUTATION_ADMIN_SWEEP_PROFILE_SESSIONS = gql`
  mutation adminSweepProfileSessions($profileId: ID!, $states: [AdminCcSessionState!]!) {
    adminSweepProfileSessions(profileId: $profileId, states: $states) {
      profileId
      sessionsDeleted
      tasksDeleted
      deletedSessionIds
      errors
    }
  }
`;

/**
 * Send an insight notification on demand for a profile — bypasses the cron,
 * the core-hours gate, the active-device skip, the per-device min-gap, AND the
 * dedup ledger (never writes it), so it's safe to fire repeatedly without
 * interfering with real delivery. `force` sends a sample even when no rated
 * insight is available. Returns full diagnostics (what was chosen, devices
 * targeted, push result) so the admin can see exactly what happened.
 */
export const useAdminTestInsightNotification = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'adminTestInsightNotification'>;
  return useMutation<T, {profileId: string; force?: boolean}>(
    MUTATION_ADMIN_TEST_INSIGHT,
    {client},
  );
};

const MUTATION_ADMIN_TEST_INSIGHT = gql`
  mutation adminTestInsightNotification($profileId: ID!, $force: Boolean) {
    adminTestInsightNotification(profileId: $profileId, force: $force) {
      ok
      profileId
      profileName
      scanned
      ratedCount
      chosenVideoId
      chosenTitle
      chosenRating
      chosenSeverity
      copyTitle
      copyBody
      devicesTargeted
      pushOk
      pushErrors
      notes
    }
  }
`;
